/**
 * Phase 54.26 — Mobile Barcode Scanner platform (frontend context).
 *
 * ONE Scanner Session per ERP tab (spec Change 1a). The session ID +
 * mobile token live in sessionStorage so route changes within the tab
 * keep the same session — no re-scanning QR when moving between POS,
 * Fiyat Sorgu, Envanter etc. A new tab creates its own session (each
 * tab has its own sessionStorage).
 *
 * WebSocket auto-reconnects with exponential backoff up to 30 s. If
 * the server returns 4404 (session expired) we clear the session so
 * the operator sees a fresh QR next time.
 *
 * `subscribe(cb)` fans out incoming barcodes to every consumer that
 * has mounted a `<ScannerButton>` — so the same phone scan drops into
 * whichever barcode-enabled input is currently listening.
 *
 * Phase 54.27.1 — Production connection hardening. Root cause of the
 * "stuck on BAĞLANIYOR" bug: the built bundle can carry a stale
 * REACT_APP_BACKEND_URL that no longer matches the production domain.
 * Now we always derive the ORIGIN from `window.location`, so:
 *   • The QR mobile URL always points to whichever domain the
 *     operator is actually using (works on preview + prod + custom
 *     domain with zero config).
 *   • The WSS URL is derived from the same origin, so ingress
 *     always routes it correctly.
 *   • Close codes 1006 (TCP failure), 1011 (server error), 4xxx
 *     (auth/session) are captured and surfaced as `lastError` so
 *     the UI can show the actual failure reason instead of an
 *     infinite spinner.
 *   • Retries cap at 4 attempts (~15 s), then the status flips to
 *     "error" — the operator gets a real explanation and a manual
 *     Retry action instead of a permanent spinner.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import usmApi from "@/lib/usmApi";

const Ctx = createContext(null);
const SESSION_KEY = "usm.scanner.session";
const MAX_RETRIES = 4;   // ~ 1 + 2 + 4 + 8 = 15 s before we give up

function loadSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.session_id || !s?.mobile_token || !s?.expires_at) return null;
    if (new Date(s.expires_at).getTime() <= Date.now()) return null;
    return s;
  } catch { return null; }
}
function saveSession(s) { try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* noop */ } }
function clearSession() { try { sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ } }

// Phase 54.27.1 — Origin is derived on the CLIENT at runtime so the
// deployed bundle can move between environments without a rebuild.
// Falls back to `REACT_APP_BACKEND_URL` if `window` is unavailable
// (SSR / test harness) — never for a live browser session.
function currentOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return process.env.REACT_APP_BACKEND_URL || "";
}
function wsOrigin() {
  return currentOrigin().replace(/^http/, "ws");
}

// Turkish diagnostic messages keyed by WebSocket close code.
function describeCloseCode(code) {
  if (code === 4401) return "Yetkisiz — oturum belirteci geçersiz.";
  if (code === 4403) return "Bu oturum başka bir kullanıcıya ait.";
  if (code === 4404) return "Oturum bulunamadı veya süresi doldu.";
  if (code === 1006) return "Bağlantı kurulamadı — sunucuya ulaşılamıyor (ingress / proxy).";
  if (code === 1011) return "Sunucu hatası — WebSocket beklenmedik şekilde kapandı.";
  if (code === 1000) return "Bağlantı kapatıldı.";
  return `WebSocket kapandı (kod ${code}).`;
}

export function ScannerSessionProvider({ children }) {
  const [session, setSession] = useState(() => loadSession());
  const [status, setStatus] = useState("idle");        // idle | connecting | live | reconnecting | expired | error
  const [mobileConnected, setMobileConnected] = useState(0);
  const [scanCount, setScanCount] = useState(0);
  const [lastError, setLastError] = useState("");
  const wsRef = useRef(null);
  const subsRef = useRef(new Set());
  const retryRef = useRef(0);
  const retryTimerRef = useRef(null);

  const emit = useCallback((code) => {
    setScanCount((n) => n + 1);
    for (const cb of subsRef.current) {
      try { cb(code); } catch (e) { console.error("scanner sub error", e); }
    }
  }, []);

  const subscribe = useCallback((cb) => {
    subsRef.current.add(cb);
    return () => subsRef.current.delete(cb);
  }, []);

  const connect = useCallback((sess) => {
    if (!sess) return;
    const jwt = localStorage.getItem("uk-usm-token") || "";
    if (!jwt) {
      setStatus("error");
      setLastError("Kimlik doğrulama belirteci yok — lütfen yeniden giriş yapın.");
      return;
    }
    setStatus((s) => s === "live" ? s : "connecting");
    const url = `${wsOrigin()}/api/usm/scan/ws/erp/${sess.session_id}?token=${encodeURIComponent(jwt)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.error("WS construction failed", e);
      setStatus("error");
      setLastError("WebSocket URL geçersiz veya karışık içerik engellendi.");
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      retryRef.current = 0;
      setStatus("live");
      setLastError("");
    };
    ws.onerror = (ev) => {
      // Browsers don't expose a useful error message for security
      // reasons; the follow-up `onclose` carries the code we care about.
      console.warn("scanner ERP ws error", ev);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "barcode" && msg.code) emit(msg.code);
        else if (msg.type === "hello") setMobileConnected(msg.mobile_connected || 0);
        else if (msg.type === "mobile_connected" || msg.type === "mobile_disconnected") setMobileConnected(msg.mobile_connected || 0);
      } catch { /* noop */ }
    };
    ws.onclose = (ev) => {
      wsRef.current = null;
      // Auth / session failures — clear and stop.
      if (ev.code === 4404 || ev.code === 4403 || ev.code === 4401) {
        clearSession();
        setSession(null);
        setStatus("expired");
        setLastError(describeCloseCode(ev.code));
        setMobileConnected(0);
        return;
      }
      // Give up after MAX_RETRIES so we never leave the UI on an
      // infinite spinner. Show the real reason so ops know why.
      if (retryRef.current >= MAX_RETRIES) {
        setStatus("error");
        setLastError(describeCloseCode(ev.code));
        return;
      }
      // Bounded exponential backoff reconnect.
      const backoff = Math.min(30000, 1000 * Math.pow(2, retryRef.current));
      retryRef.current += 1;
      setStatus("reconnecting");
      setLastError(`${describeCloseCode(ev.code)} · Yeniden deneniyor (${retryRef.current}/${MAX_RETRIES})…`);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        if (sessionStorage.getItem(SESSION_KEY)) connect(loadSession());
      }, backoff);
    };
  }, [emit]);

  // Connect on mount if we already have a session.
  useEffect(() => {
    if (session) connect(session);
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, []);

  const createSession = useCallback(async () => {
    // Reuse existing session if still valid.
    const existing = loadSession();
    if (existing) { setSession(existing); retryRef.current = 0; setLastError(""); connect(existing); return existing; }
    setLastError("");
    let s;
    try {
      const r = await usmApi.post("/scan/sessions");
      s = r.data;
    } catch (e) {
      console.error("create scanner session failed", e);
      setStatus("error");
      setLastError(e?.response?.data?.detail || "Oturum oluşturulamadı — sunucuya ulaşılamıyor.");
      throw e;
    }
    saveSession(s);
    setSession(s);
    setStatus("connecting");
    retryRef.current = 0;
    connect(s);
    return s;
  }, [connect]);

  const disconnect = useCallback(async () => {
    const s = session;
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
    clearSession();
    setSession(null);
    setStatus("idle");
    setLastError("");
    setMobileConnected(0);
    if (s?.session_id) {
      try { await usmApi.delete(`/scan/sessions/${s.session_id}`); } catch { /* noop */ }
    }
  }, [session]);

  // Manual retry — used when status is "error" and the operator
  // taps "Yeniden Dene" in the dialog.
  const retry = useCallback(() => {
    retryRef.current = 0;
    setLastError("");
    const s = loadSession() || session;
    if (s) { setSession(s); connect(s); }
    else { createSession().catch(() => { /* handled inside */ }); }
  }, [session, connect, createSession]);

  // Phase 54.27.1 — Mobile QR URL always points to the SAME origin
  // the operator is on. Guarantees the phone can reach the SPA
  // regardless of what REACT_APP_BACKEND_URL was baked with.
  const mobileUrl = useMemo(() => {
    if (!session) return "";
    return `${currentOrigin()}/scan/${session.session_id}?t=${session.mobile_token}`;
  }, [session]);

  const value = useMemo(() => ({
    session, status, mobileConnected, scanCount, mobileUrl, lastError,
    createSession, disconnect, subscribe, retry,
  }), [session, status, mobileConnected, scanCount, mobileUrl, lastError, createSession, disconnect, subscribe, retry]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScannerSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useScannerSession must be inside ScannerSessionProvider");
  return v;
}
